<template>
  <div class="game-page">
    <!-- Preloading Screen -->
    <div
      v-if="preloading"
      class="game-loading-screen"
      :style="preloadedBg ? { backgroundImage: `url(${preloadedBg})` } : undefined"
    >
      <div v-if="preloadedLogo" class="loading-logo">
        <img :src="preloadedLogo" :alt="slug" class="loading-game-logo" />
      </div>
      <div class="loading-bar-track">
        <div class="loading-bar-fill" :style="{ width: loadProgress + '%' }"></div>
      </div>
      <div class="loading-text">{{ $t('player.game.loading', { title: gameTitle }) }}</div>
    </div>

    <!-- Game Component -->
    <component :is="gameComponent" v-if="gameComponent && !preloading" />

    <!-- Not Found -->
    <div v-else-if="!gameComponent && !preloading" class="game-not-found">
      <p>{{ $t('player.game.not_available', { slug: slug }) }}</p>
      <button class="btn-back-lobby" @click="router.push({ name: 'player.lobby' })">
        ← {{ $t('player.game.back_to_lobby') }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { updateSeo } from '@/composables/useSeo'
import BaccaratGame from '@/components/games/BaccaratGame.vue'
import BlackjackGame from '@/components/games/BlackjackGame.vue'
import CrashGame from '@/components/games/CrashGame.vue'
import DiceGame from '@/components/games/DiceGame.vue'
import HiloGame from '@/components/games/HiloGame.vue'
import KenoGame from '@/components/games/KenoGame.vue'
import MinesGame from '@/components/games/MinesGame.vue'
import PlinkoGame from '@/components/games/PlinkoGame.vue'
import RouletteGame from '@/components/games/RouletteGame.vue'
import VideoPokerGame from '@/components/games/VideoPokerGame.vue'
import SlotGame from '@/components/games/SlotGame.vue'

const route = useRoute()
const router = useRouter()

const slug = computed(() => route.params.slug as string)

const GAME_COMPONENTS: Record<string, unknown> = {
  baccarat: BaccaratGame,
  blackjack: BlackjackGame,
  crash: CrashGame,
  dice: DiceGame,
  hilo: HiloGame,
  keno: KenoGame,
  mines: MinesGame,
  plinko: PlinkoGame,
  roulette: RouletteGame,
  'video-poker': VideoPokerGame,
  'egyptian-gold': SlotGame,
  'neon-vegas': SlotGame,
  'dragon-fortune': SlotGame,
  'fruit-classic': SlotGame,
  'ocean-treasures': SlotGame,
  'wild-west-gold': SlotGame,
  'crypto-miner': SlotGame,
  'mythic-gods': SlotGame,
  'lucky-leprechaun': SlotGame,
  'space-gems': SlotGame,
}

const TABLE_GAMES = ['blackjack', 'baccarat', 'roulette', 'video-poker']
const INSTANT_GAMES = ['dice', 'mines', 'crash', 'plinko', 'keno', 'hilo']

function getGameCategory(gameSlug: string): string {
  if (TABLE_GAMES.includes(gameSlug)) return 'table'
  if (INSTANT_GAMES.includes(gameSlug)) return 'instant'
  return 'slots'
}

function getGameTitle(gameSlug: string): string {
  return gameSlug.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

const gameComponent = computed(() => GAME_COMPONENTS[slug.value] ?? null)
const gameTitle = computed(() => getGameTitle(slug.value))

// Update document title when playing a game
watch(gameTitle, (title) => {
  if (title) {
    updateSeo(`Play ${title}`)
  }
}, { immediate: true })

const preloading = ref(true)
const loadProgress = ref(0)
const preloadedBg = ref<string | null>(null)
const preloadedLogo = ref<string | null>(null)

function probeImage(src: string): Promise<string | null> {
  return new Promise((resolve) => {
    const img = new Image()
    img.onload = () => resolve(src)
    img.onerror = () => resolve(null)
    img.src = src
  })
}

async function preloadAssets() {
  if (!gameComponent.value) {
    preloading.value = false
    return
  }

  const dir = slug.value.replace(/-/g, '_')
  const category = getGameCategory(slug.value)
  const assetDir = category === 'slots'
    ? `slots/${dir}`
    : category === 'table'
      ? `table/${dir}`
      : `instant/${dir}`

  loadProgress.value = 20

  const [bg, logo] = await Promise.all([
    probeImage(`/assets/${assetDir}/background.jpg`),
    probeImage(`/assets/${assetDir}/logo.png`),
  ])

  if (bg) preloadedBg.value = bg
  if (logo) preloadedLogo.value = logo
  loadProgress.value = 100

  await new Promise(r => setTimeout(r, 300))
  preloading.value = false
}

watch(slug, () => {
  preloading.value = true
  loadProgress.value = 0
  preloadedBg.value = null
  preloadedLogo.value = null
  preloadAssets()
}, { immediate: true })
</script>

<style scoped>
.game-page {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.game-not-found {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  gap: 16px;
  color: var(--text-secondary, #b1bad3);
  font-size: 16px;
}

.btn-back-lobby {
  padding: 11px 22px;
  background: var(--accent-primary, #00e701);
  color: var(--color-on-accent, #000);
  font-size: 14px;
  font-weight: 700;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  font-family: inherit;
}

.btn-back-lobby:hover {
  background: var(--accent-primary-hover, #00cc01);
}

/* Loading Screen */
.game-loading-screen {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  gap: 20px;
  background-color: var(--bg-primary, #0f1923);
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  animation: fadeIn 0.2s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.loading-logo {
  margin-bottom: 8px;
}

.loading-game-logo {
  max-width: 240px;
  max-height: 80px;
  object-fit: contain;
  filter: drop-shadow(0 4px 12px rgba(0, 0, 0, 0.6));
}

.loading-bar-track {
  width: 200px;
  height: 4px;
  background: var(--bg-tertiary, #213743);
  border-radius: 2px;
  overflow: hidden;
}

.loading-bar-fill {
  height: 100%;
  background: var(--accent-primary, #00e701);
  border-radius: 2px;
  transition: width 0.3s ease;
}

.loading-text {
  font-size: 13px;
  color: var(--text-secondary, #b1bad3);
  font-weight: 500;
}
</style>
